/***
 * File name: adminProfileMixin.js
 *
 * Program Description:
 * This file contains all the reusable functions and data with other components
 * related to the user profile
 *
 * Inlcudes the following:
 * - Data schema of the user profile
 * - Meta variables and dialog indicators
 * - Mixins used within components
 * - Alert Mixin conatains functions to easily trigger alerts in the components
 * - Functions for fetching and manipulation of user profile information
 *
 */
import { metaMixin } from '@/meta'
import alertMixin from '@/mixins/alertMixin'
import { capitalize, dataURLToFile, uploadID } from '@/utils/helpers'
import users from '@/utils/webapi/users'

export default {
  name: 'AdminProfile',
  data: () => ({
    userId: '',
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    userRole: [],
    division: '',
    designation: '',
    contactNumber: '',
    sex: '',
    userType: '',
    sector: '',
    subsector: undefined,
    organization: '',
    $meta: {
      readOnly: true,
      isLoading: false
    },
    showDialog: false,
    deactivationReason: '',
    status: '',
    reason: 'reason',
    reasonModal: {
      label: '',
      header: '',
      modal: false,
      cancel: '',
      continue: ''
    }
  }),
  mixins: [metaMixin, alertMixin],
  computed: {
    updateInfo () {
      const { $meta, createdBy, updatedBy, dateCreated, status, userRole, ...newInfo } = this.$data
      return newInfo
    },
    isPic () {
      return this.$data.userRole.includes('pic')
    }
  },
  watch: {
  },
  methods: {
    capitalize,
    confirmDialog (action) {
      const label = this.capitalize(action)
      this.reason = 'action'
      this.$store.dispatch('showDialog', {
        title: `${label} User`,
        message: `Are you sure you want to ${action} this profile?`,
        buttonLabel: label,
        buttonColor: 'primary',
        callback: () => { this[`${action}`]() }
      })
    },
    async getProfileWrap (fn) {
      this.$data.$meta.isLoading = true
      await this.$s.ready
      this.clearProfile()
      const resp = await fn()
      const jData = await resp.json()
      if (resp.ok) {
        const profile = jData.profile || jData.entry
        await this.getImages(profile)
        for (const k in profile) {
          await this.setValue(k, profile)
        }
      } else {
        this.bubbleAlert({
          message: this.$data.$meta.errors.fetchProfile,
          type: 'error'
        })
      }
      this.$data.$meta.isLoading = false
    },
    async getImages (profile) {
      const { _id } = profile
      if (profile.userType === 'ProponentExpert') {
        profile.idPictureFront && await this.fetchID(_id, 'front', profile.idPictureFront)
        profile.idPictureBack && await this.fetchID(_id, 'back', profile.idPictureBack)
      }
    },
    async fetchID (id, side, idPicture) {
      const { filename, mimetype } = idPicture
      const data = await users.getID(id, side)
      const value = await data.json()
      this.$data[`idPicture${capitalize(`${side}`)}`] = await dataURLToFile(value, filename, mimetype)
    },
    async updateProfileWrap (fn, info) {
      this.reason = 'update'
      await this.$s.ready
      this.$data.$meta.isLoading = true
      const resp = await fn()
      const jData = await resp.json()
      if (resp.ok) {
        this.$data.$meta.readOnly = true
        const { _id } = jData.userData
        await this.upload(info, _id)
        this.getProfile()
        this.$store.dispatch('showAlert', {
          message: 'Your profile has been successfully updated.',
          type: 'success'
        })
        if (this.operation && this.operation !== 'view') {
          this.$router.push({
            name: 'UserProfileView',
            params: {
              userRole: this.$route.params.userRole,
              operation: 'view',
              id: this.$route.params.id
            }
          })
        }
      } else {
        this.$store.dispatch('showAlert', {
          message: jData.error || this.$data.$meta.errors.updateProfile,
          type: 'error'
        })
      }
      this.$data.$meta.isLoading = false
    },
    cancel () {
      if (this.operation === 'edit') this.$router.go(-1)
      else {
        this.$data.$meta.readOnly = true
        this.getProfile()
      }
    },
    clearProfile () {
      for (const k in this.$data.$meta.raw) {
        this.$data[`${k}`] = this.$data.$meta.raw[`${k}`]
      }
    },
    async setValue (k, profile) {
      switch (k) {
        case '_status':
          this.status = capitalize(profile[`${k}`])
          break
        case 'birthdate':
          this[`${k}`] = profile[`${k}`].split('T')[0]
          break
        case 'dateOfEmployment':
          this[`${k}`] = profile[`${k}`].split('T')[0]
          break
        case 'address':
          await this.setAddress(profile)
          break
        case 'userRole':
          if (profile[`${k}`].includes(',')) {
            this[`${k}`] = profile[`${k}`].split(', ')
          } else {
            this[`${k}`] = [profile[`${k}`]]
          }
          break
        case 'idPictureFront':
          if (profile[`${k}`] && profile[`${k}`].fieldname) break
          else this[`${k}`] = []
          break
        case 'idPictureBack':
          if (profile[`${k}`] && profile[`${k}`].fieldname) break
          else this[`${k}`] = []
          break
        case '_id':
          this.userId = profile[`${k}`]
          break
        default:
          if (profile[`${k}`]) {
            this[`${k}`] = profile[`${k}`]._id || profile[`${k}`]
          }
          break
      }
    },
    vuetifyOpts (entries) {
      return entries.map((i) => ({
        value: i._id,
        text: i.name
      }))
    },
    saveDate (date) {
      this.$refs.menu.save(date)
    },
    async upload (info, id) {
      const frontImageFile = uploadID('front', info.idPictureFront)
      await users.uploadID(id, 'front', frontImageFile)
      const backImageFile = uploadID('back', info.idPictureBack)
      await users.uploadID(id, 'back', backImageFile)
      return true
    }
  }
}
